import axios, { AxiosError } from 'axios';
import { isJWTExpired } from '@global-contexts/utils/authUtils';
import { newId } from './newId';
import { withSessionRefreshLock } from './sessionRefreshLock';

const SESSION_KEYS = ['__token', '__refresh-token', '__company_id', '__company_name', '__user_id', '__user_email'];
const GENERATION_KEY = '__session-generation';
export const SESSION_EVENT = 'auth-session-changed';

export const sessionSnapshot = () => ({
  access: localStorage.getItem('__token'),
  refresh: localStorage.getItem('__refresh-token'),
  generation: localStorage.getItem(GENERATION_KEY),
  impersonation: localStorage.getItem('__impersonation')
});
export type SessionSnapshot = ReturnType<typeof sessionSnapshot>;
export const isCurrentSession = (snapshot: SessionSnapshot) => {
  const current = sessionSnapshot();
  return Object.keys(snapshot).every((key) => snapshot[key] === current[key]);
};
export const advanceSessionGeneration = () => localStorage.setItem(GENERATION_KEY, newId());

export const invalidateSession = (snapshot?: SessionSnapshot) => {
  if ((snapshot && !isCurrentSession(snapshot)) || window.location.pathname === '/login') {
    return;
  }
  advanceSessionGeneration();
  for (const key of SESSION_KEYS) {
    localStorage.removeItem(key);
  }
  window.dispatchEvent(new Event(SESSION_EVENT));
  if (process.env.NODE_ENV !== 'test') {
    window.location.href = '/login';
  }
};

const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_HOST,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10_000
});
// Renewal errors are classified before any session invalidation, unlike ordinary API errors.
export const refreshTokenTransport = (code: string | null, refresh: string | null) =>
  refreshClient.post(
    'auth/token/refresh/',
    { code, refresh },
    { headers: { Authorization: code ? `Bearer ${code}` : '' } }
  );

type RenewalResult = 'ready' | 'recoverable' | 'ended';
// Retained logout credentials must not start another rotation before local cleanup.
const logoutWaits = new Map<string | null, Promise<RenewalResult>>();
export const deferRenewalDuringLogout = (generation: string | null, completion: Promise<unknown>) => {
  const wait = completion
    .then(
      () => 'ended' as const,
      () => 'ended' as const
    )
    .finally(() => {
      if (logoutWaits.get(generation) === wait) {
        logoutWaits.delete(generation);
      }
    });
  logoutWaits.set(generation, wait);
};
let pending: Promise<RenewalResult> | undefined;
let forceRequested = false;
let renewalStarted = false;
const forcedFollowups = new Map<string | null, Promise<RenewalResult>>();
let pendingGeneration: string | null | undefined;
export const isSessionRenewing = () =>
  Boolean(pending && sessionSnapshot().access && pendingGeneration === sessionSnapshot().generation);
let recoveringFor: SessionSnapshot | undefined;
export const isSessionRecovering = () => Boolean(recoveringFor && isCurrentSession(recoveringFor));
const notify = (next: boolean) => {
  recoveringFor = next ? sessionSnapshot() : undefined;
  window.dispatchEvent(new Event(SESSION_EVENT));
};

export const renewSession = (force = false, transport = refreshTokenTransport): Promise<RenewalResult> => {
  const logout = logoutWaits.get(sessionSnapshot().generation);
  if (logout) {
    return logout;
  }
  if (pending) {
    if (!force) {
      return pending;
    }
    const caller = sessionSnapshot();
    if (!renewalStarted && caller.generation === pendingGeneration) {
      forceRequested = true;
      return pending;
    }
    // MFA can change claims after a refresh starts, including for a replacement login.
    const existing = forcedFollowups.get(caller.generation);
    if (existing) {
      return existing;
    }
    const precedingGeneration = pendingGeneration;
    const followup = pending
      .then((result) => {
        if (caller.generation !== sessionSnapshot().generation) {
          return result;
        }
        if (result !== 'ready' && caller.generation === precedingGeneration) {
          return result;
        }
        return renewSession(true, transport);
      })
      .finally(() => {
        forcedFollowups.delete(caller.generation);
      });
    forcedFollowups.set(caller.generation, followup);
    return followup;
  }
  const requested = sessionSnapshot();
  pendingGeneration = requested.generation;
  forceRequested = force || isSessionRecovering();
  renewalStarted = false;
  let attempted = requested;
  pending = withSessionRefreshLock(async (guard) => {
    renewalStarted = true;
    const snapshot = sessionSnapshot();
    attempted = snapshot;
    if (!snapshot.access || snapshot.impersonation) {
      return 'ended' as const;
    }
    if (requested.generation !== snapshot.generation || requested.impersonation !== snapshot.impersonation) {
      return 'ready' as const;
    }
    if (
      !forceRequested &&
      (!isCurrentSession(requested) || !isJWTExpired(snapshot.access).accessTokenWillExpireWithinBufferTime)
    ) {
      return 'ready' as const;
    }
    try {
      const { data } = await transport(snapshot.access, snapshot.refresh);
      if (!isCurrentSession(snapshot)) {
        return 'ready' as const;
      }
      if (typeof data.access !== 'string' || typeof data.refresh !== 'string') {
        return 'recoverable' as const;
      }
      const owned = await guard(() => {
        if (isCurrentSession(snapshot)) {
          localStorage.setItem('__token', data.access);
          localStorage.setItem('__refresh-token', data.refresh);
        }
      });
      return owned || !isCurrentSession(snapshot) ? ('ready' as const) : ('recoverable' as const);
    } catch (error) {
      if (!isCurrentSession(snapshot)) {
        return 'ready' as const;
      }
      const status = (error as AxiosError).response?.status;
      if (status === 400 || status === 401 || status === 403) {
        let ended = false;
        const owned = await guard(() => {
          if (isCurrentSession(snapshot)) {
            invalidateSession(snapshot);
            ended = true;
          }
        });
        if (ended) {
          return 'ended' as const;
        }
        return owned || !isCurrentSession(snapshot) ? ('ready' as const) : ('recoverable' as const);
      }
      return 'recoverable' as const;
    }
  })
    .catch(() => (isCurrentSession(attempted) ? ('recoverable' as const) : ('ready' as const)))
    .then((result) => {
      if (result === 'recoverable') {
        if (isCurrentSession(attempted) && attempted.access) {
          notify(true);
        }
      } else if (result === 'ended' || requested.generation === sessionSnapshot().generation) {
        notify(false);
      }
      return result;
    })
    .finally(() => {
      pending = undefined;
      renewalStarted = false;
      window.dispatchEvent(new Event(SESSION_EVENT));
    });
  window.dispatchEvent(new Event(SESSION_EVENT));
  return pending;
};

export const handleAuthenticationFailure = async (snapshot: SessionSnapshot | undefined, accessExpired = false) => {
  if (!snapshot?.access || !isCurrentSession(snapshot)) {
    return;
  }
  // Wait for an already running rotation before deciding that its old access token is invalid.
  if (pending) {
    await pending;
  }
  if (!isCurrentSession(snapshot)) {
    return;
  }
  // Access expiry alone does not end a renewable session. Run outside the lock:
  // renewSession acquires the same lock, and the failed request must never replay.
  if (
    accessExpired &&
    snapshot.refresh &&
    !snapshot.impersonation &&
    isJWTExpired(snapshot.access).accessTokenIsExpired
  ) {
    if (!isSessionRecovering()) {
      await renewSession();
    }
    return;
  }
  await withSessionRefreshLock(async (guard) => {
    await guard(() => {
      invalidateSession(snapshot);
    });
  }).catch(() => {
    if (isCurrentSession(snapshot)) {
      notify(true);
    }
  });
};

export const assertRequestSession = (snapshot: SessionSnapshot) => {
  const current = sessionSnapshot();
  if (snapshot.generation !== current.generation || snapshot.impersonation !== current.impersonation) {
    throw new axios.CanceledError('The session changed. Please retry from the current account.');
  }
};

export const ensureSessionForRequest = async () => {
  const snapshot = sessionSnapshot();
  if (!snapshot.access || snapshot.impersonation) {
    return snapshot;
  }
  if (!isSessionRecovering() && !isJWTExpired(snapshot.access).accessTokenIsExpired) {
    return snapshot;
  }
  const result = await renewSession();
  assertRequestSession(snapshot);
  if (result !== 'ready' || !sessionSnapshot().access || isJWTExpired(sessionSnapshot().access).accessTokenIsExpired) {
    throw new axios.CanceledError('Session renewal is unavailable. Please retry when the connection recovers.');
  }
  return snapshot;
};
